import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EditIcon, Trash2Icon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = ["AGUARDANDO_LAUDO", "PRONTO_DESCARGA", "EM_ENVASE", "CONCLUIDO"];

export default function BulkReceiptEditModal({ open, receipt, products = [], onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (receipt) {
      setForm({
        nf_recebimento: receipt.nf_recebimento || "",
        remessa: receipt.remessa || "",
        fornecedor: receipt.fornecedor || "",
        lote_fabricante: receipt.lote_fabricante || "",
        codigo_produto: receipt.codigo_produto ? String(receipt.codigo_produto) : "",
        densidade: receipt.densidade ?? "",
        peso_bruto: receipt.peso_bruto ?? "",
        status: receipt.status || "AGUARDANDO_LAUDO",
      });
    }
  }, [receipt]);

  const onProductChange = (codigo) => {
    const prod = products.find((p) => String(p.codigo) === String(codigo));
    setForm((f) => ({
      ...f,
      codigo_produto: codigo,
      densidade: prod ? String(prod.densidade) : f.densidade,
    }));
  };

  const previewVolume = (() => {
    const p = parseFloat(form.peso_bruto);
    const d = parseFloat(form.densidade);
    if (!p || !d) return "—";
    return (p / d).toFixed(2);
  })();

  const submit = async () => {
    if (!receipt) return;
    setBusy(true);
    try {
      const payload = {};
      const changed = (k, cur, orig) => {
        if (cur === "" || cur === null || cur === undefined) return;
        if (String(cur) !== String(orig ?? "")) payload[k] = cur;
      };
      changed("nf_recebimento", form.nf_recebimento, receipt.nf_recebimento);
      changed("remessa", form.remessa, receipt.remessa);
      changed("fornecedor", form.fornecedor, receipt.fornecedor);
      changed("lote_fabricante", form.lote_fabricante, receipt.lote_fabricante);
      changed("status", form.status, receipt.status);
      // Produto
      if (String(form.codigo_produto) !== String(receipt.codigo_produto ?? "")) {
        const prod = products.find((p) => String(p.codigo) === String(form.codigo_produto));
        payload.codigo_produto = parseInt(form.codigo_produto, 10);
        if (prod) {
          payload.nome_produto = prod.nome;
          payload.funcao_quimica = prod.funcao_quimica;
        }
      }
      if (parseFloat(form.densidade) !== parseFloat(receipt.densidade)) payload.densidade = parseFloat(form.densidade);
      if (parseFloat(form.peso_bruto) !== parseFloat(receipt.peso_bruto)) payload.peso_bruto = parseFloat(form.peso_bruto);
      if (!Object.keys(payload).length) {
        toast.info("Nenhuma alteração para salvar");
        setBusy(false);
        return;
      }
      await api.patch(`/bulk-receipts/${receipt.id}`, payload);
      toast.success("Recebimento atualizado");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!receipt) return;
    if (!window.confirm(`Excluir definitivamente o recebimento ${receipt.remessa}? Esta ação não pode ser desfeita.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/bulk-receipts/${receipt.id}`);
      toast.success("Recebimento excluído");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  if (!receipt) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#14151A] border-border rounded-none max-w-3xl" data-testid="bulk-receipt-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <EditIcon className="w-5 h-5 text-[#FFD60A]" /> Editar Recebimento · <span className="font-mono text-[#FFD60A]">{receipt.remessa}</span>
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Corrige dados do caminhão a granel. Ao mudar peso ou densidade, o volume teórico é recalculado automaticamente.
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="NF Recebimento">
            <Input data-testid="bre-nf" value={form.nf_recebimento} onChange={(e) => setForm({ ...form, nf_recebimento: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="Remessa">
            <Input data-testid="bre-remessa" value={form.remessa} onChange={(e) => setForm({ ...form, remessa: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="Fornecedor" full>
            <Input data-testid="bre-fornecedor" value={form.fornecedor} onChange={(e) => setForm({ ...form, fornecedor: e.target.value })} className="rounded-none bg-[#0A0A0C] h-10" />
          </Field>
          <Field label="Lote Fabricante">
            <Input data-testid="bre-lote" value={form.lote_fabricante} onChange={(e) => setForm({ ...form, lote_fabricante: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="Produto">
            <Select value={String(form.codigo_produto || "")} onValueChange={onProductChange}>
              <SelectTrigger data-testid="bre-produto" className="rounded-none bg-[#0A0A0C] h-10 border-border">
                <SelectValue placeholder="Selecionar" />
              </SelectTrigger>
              <SelectContent className="bg-[#14151A] border-border rounded-none max-h-72">
                {products.map((p) => (
                  <SelectItem key={p.id} value={String(p.codigo)}>
                    <span className="font-mono text-xs text-[#FFD60A] mr-2">{p.codigo}</span>{p.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Densidade (kg/L)">
            <Input data-testid="bre-densidade" type="number" step="0.001" value={form.densidade} onChange={(e) => setForm({ ...form, densidade: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="Peso Bruto (kg)">
            <Input data-testid="bre-peso" type="number" step="0.01" value={form.peso_bruto} onChange={(e) => setForm({ ...form, peso_bruto: e.target.value })} className="rounded-none bg-[#0A0A0C] font-mono h-10" />
          </Field>
          <Field label="Status">
            <select
              data-testid="bre-status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full rounded-none bg-[#0A0A0C] border border-border h-10 px-2 text-sm font-mono"
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </Field>

          <div className="md:col-span-2 border-t border-border/50 mt-2 pt-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Volume Teórico (recalculado)</div>
              <div className="font-mono text-2xl text-[#00E5FF]" data-testid="bre-volume-preview">{previewVolume} L</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Volume Envasado</div>
              <div className="font-mono text-lg">{receipt.volume_envasado || 0} L</div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2 gap-2">
          {isAdmin && (
            <Button
              onClick={doDelete}
              disabled={deleting || busy || (receipt.volume_envasado || 0) > 0}
              variant="outline"
              data-testid="bre-delete"
              title={(receipt.volume_envasado || 0) > 0 ? "Já consumido em envase — não pode excluir" : "Excluir recebimento"}
              className="rounded-none border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 mr-auto"
            >
              <Trash2Icon className="w-4 h-4 mr-1" /> {deleting ? "Excluindo..." : "Excluir"}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-none border-border">Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="bre-submit" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#FFD60A]/85 font-bold uppercase tracking-wider">
            {busy ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
